import {
  createSlice,
  createAsyncThunk,
  type PayloadAction,
} from "@reduxjs/toolkit";
import {
  shipmentAPI,
  publicShipmentAPI,
  userSettingsAPI,
} from "../../services/api";
import {
  CreateShipmentData,
  UpdateShipmentData,
  ShipmentState,
  Column,
  AttachmentMeta,
} from "../../types";

export const getShipments = createAsyncThunk(
  "shipment/getShipments",
  async (params: any = {}, { rejectWithValue }) => {
    try {
      const response = await shipmentAPI.getShipments(params);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch Shipments",
      );
    }
  },
);

export const getShipmentsForGroups = createAsyncThunk(
  "shipment/getShipmentsForGroups",
  async (params: any = {}, { rejectWithValue }) => {
    try {
      const response = await shipmentAPI.getShipmentsForGroups(params);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch Shipments",
      );
    }
  },
);

export const getShipment = createAsyncThunk(
  "shipment/getShipment",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await shipmentAPI.getShipment(id);
      return response.data.data.shipment;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch shipment",
      );
    }
  },
);

export const getShipmentPublic = createAsyncThunk(
  "shipment/getShipmentPublic",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await publicShipmentAPI.getShipment(id);
      return response.data.data.shipment;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch shipment",
      );
    }
  },
);

export const getNextShipmentId = createAsyncThunk(
  "shipment/getNextShipmentId",
  async (_, { rejectWithValue }) => {
    try {
      const response = await shipmentAPI.getNextShipmentId();
      return response.data.data.shipmentId;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch next shipment Id",
      );
    }
  },
);

export const deleteShipment = createAsyncThunk(
  "shipment/deleteShipment",
  async (id: string, { rejectWithValue }) => {
    try {
      await shipmentAPI.deleteShipment(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete Shipment",
      );
    }
  },
);

export const deleteMultipleShipments = createAsyncThunk(
  "shipment/deleteMultipleShipments",
  async (ids: string[], { rejectWithValue }) => {
    try {
      await shipmentAPI.deleteMultipleShipments(ids);
      return ids;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete shipments",
      );
    }
  },
);

export const generateShipmentXML = createAsyncThunk(
  "shipment/generateShipmentXML",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await shipmentAPI.getShipmentXML(id);
      const blob = new Blob([response.data], { type: "application/xml" });

      const contentDisposition = response.headers["content-disposition"];

      let fileName = "CMR.xml";

      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+)"?/);
        if (match) fileName = match[1];
      }

      const url = window.URL.createObjectURL(blob);
      // Open in a new tab
      window.open(url, "_blank");

      const link = document.createElement("a");
      link.href = url;

      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();

      // Delay revoking URL to ensure new tab has time to load it
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);

      return true;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to generate CMR XML",
      );
    }
  },
);

export const fetchShipmentXMLFile = createAsyncThunk(
  "shipment/fetchShipmentXMLFile",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await shipmentAPI.getShipmentXML(id);
      const blob = new Blob([response.data], { type: "application/xml" });

      const contentDisposition = response.headers["content-disposition"];
      let fileName = `CMR_${id}.xml`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+)"?/);
        if (match) fileName = match[1];
      }

      // Create File object from blob for attachment
      const file = new File([blob], fileName, { type: "application/xml" });

      // Return the file data for attachment
      return {
        file,
        fileName,
        size: blob.size,
        xmlContent: response.data,
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch CMR XML",
      );
    }
  },
);

export const createWayBill = createAsyncThunk(
  "shipment/createWayBill",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await shipmentAPI.createWayBill(id);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to Send eFti",
      );
    }
  },
);

export const createShipment = createAsyncThunk(
  "shipment/createShipment",
  async (
    { shipment }: { shipment: CreateShipmentData },
    { rejectWithValue },
  ) => {
    try {
      const response = await shipmentAPI.createShipment(shipment);
      return response.data.data.shipment;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create shipment",
      );
    }
  },
);

// Fetches a sanitized snapshot of a shipment's creation-relevant fields to
// prefill the create-shipment form. Read-only — does not create anything, so
// it deliberately does not touch `shipments`/`currentShipment` in state; the
// caller navigates to the create form and passes the returned data along.
export const getShipmentCopyData = createAsyncThunk(
  "shipment/getShipmentCopyData",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await shipmentAPI.getShipmentCopyData(id);
      return response.data.data.shipment;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch shipment copy data",
      );
    }
  },
);

export const assignShipmentToDriverApp = createAsyncThunk(
  "shipment/assignShipmentToDriverApp",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await shipmentAPI.assignShipmentToDriverApp(id);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to copy shipment",
      );
    }
  },
);

export const removeShipmentFromDriverApp = createAsyncThunk(
  "shipment/removeShipmentFromDriverApp",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await shipmentAPI.removeShipmentFromDriverApp(id);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to copy shipment",
      );
    }
  },
);

export const updateShipment = createAsyncThunk(
  "shipment/updateShipment",
  async (
    { id, shipmentData }: { id: string; shipmentData: UpdateShipmentData },
    { rejectWithValue },
  ) => {
    try {
      const response = await shipmentAPI.updateShipment(id, shipmentData);
      return response.data.data.shipment;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update shipment",
      );
    }
  },
);

export const sendCmrAttachments = createAsyncThunk(
  "shipment/sendCmrAttachments",
  async (formData: any, { rejectWithValue }) => {
    try {
      const response = await shipmentAPI.sendCmrAttachments(formData);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to send CMR attachments",
      );
    }
  },
);

export const downloadShipmentAttachment = createAsyncThunk(
  "shipment/downloadShipmentAttachment",
  async (
    {
      shipmentId,
      attachmentId,
      previewOnly = false, // optional flag for previewing
    }: { shipmentId: string; attachmentId: string; previewOnly?: boolean },
    { rejectWithValue },
  ) => {
    try {
      const response = await shipmentAPI.downloadAttachment(
        shipmentId,
        attachmentId,
      );

      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });

      const contentDisposition = response.headers["content-disposition"];
      let filename = "downloaded-file";
      if (contentDisposition) {
        const utfMatch = contentDisposition.match(/filename\*=UTF-8''(.+)/);
        const asciiMatch = contentDisposition.match(/filename="(.+?)"/);

        if (utfMatch) {
          filename = decodeURIComponent(utfMatch[1]);
        } else if (asciiMatch) {
          filename = asciiMatch[1];
        }
      }

      const fileURL = URL.createObjectURL(blob);

      // 🔍 If previewOnly, open in new tab
      if (previewOnly) {
        window.open(fileURL, "_blank");
      } else {
        const link = document.createElement("a");
        link.href = fileURL;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      return { success: true, filename };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to download attachment",
      );
    }
  },
);

export const getShipmentColumns = createAsyncThunk(
  "shipment/getShipmentColumns",
  async (_, { rejectWithValue }) => {
    try {
      const response = await userSettingsAPI.getShipmentColumns();
      return response.data.data.columns;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch shipment columns",
      );
    }
  },
);

export const updateShipmentColumns = createAsyncThunk(
  "shipment/updateShipmentColumns",
  async (columns: Column[], { rejectWithValue }) => {
    try {
      const response = await userSettingsAPI.updateShipmentColumns(columns);
      return response.data.data.columns;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update shipment columns",
      );
    }
  },
);

export const resetShipmentColumns = createAsyncThunk(
  "shipment/resetShipmentColumns",
  async (_, { rejectWithValue }) => {
    try {
      const response = await userSettingsAPI.resetShipmentColumns();
      return response.data.data.columns;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to reset shipment columns",
      );
    }
  },
);

export const toggleAttachmentPublic = createAsyncThunk(
  "shipment/toggleAttachmentPublic",
  async (
    { shipmentId, attachmentId }: { shipmentId: string; attachmentId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await shipmentAPI.toggleAttachmentPublic(
        shipmentId,
        attachmentId,
      );
      return response.data.data; // { attachmentId, isPublic }
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to toggle attachment",
      );
    }
  },
);

export const sendDeliveryOtp = createAsyncThunk(
  "shipment/sendDeliveryOtp",
  async (
    {
      shipmentId,
      recipientEmail,
    }: { shipmentId: string; recipientEmail: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await shipmentAPI.sendDeliveryOtp(
        shipmentId,
        recipientEmail,
      );
      return {
        shipmentId,
        sentTo: response.data.data.sentTo,
        updatedAt: response.data.data.updatedAt as string | undefined,
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to send delivery OTP",
      );
    }
  },
);

// Public
export const uploadPublicAttachment = createAsyncThunk(
  "shipment/uploadPublicAttachment",
  async (
    { shipmentId, files }: { shipmentId: string; files: File[] },
    { rejectWithValue },
  ) => {
    try {
      const formData = new FormData();
      files.forEach((file) => formData.append("attachments[]", file));
      const response = await publicShipmentAPI.uploadAttachments(
        shipmentId,
        formData,
      );
      return response.data.data.attachments as AttachmentMeta[];
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Upload failed");
    }
  },
);

const initialState: ShipmentState = {
  shipments: [],
  currentShipment: null,
  columns: [],
  loading: false,
  sendEmailLoading: false,
  fetchNextShipmentLoading: false,
  currentShipmentLoadingForNotification: false,
  error: null,
  otpLoading: false,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  },
  statusCounts: {
    Order: 0,
    ReadyForPickup: 0,
    AwaitingPickup: 0,
    InTransit: 0,
    Completed: 0,
  },
  listRequestId: null,
};

const shipmentSlice = createSlice({
  name: "shipment",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentShipment: (state) => {
      state.currentShipment = null;
      state.currentShipmentLoadingForNotification = false;
    },
    setColumns: (state, action) => {
      state.columns = action.payload;
    },
    setLoading: (state) => {
      state.loading = true;
    },

    /**
     * A driver (or another dispatcher) moved a shipment along. Patches the row
     * in place rather than refetching: the list's query params are assembled
     * from a dozen filter states inside ShipmentsList, and re-deriving them here
     * would duplicate that logic.
     *
     * `statusCounts` is adjusted alongside so the status chips stay truthful.
     * One imprecision remains by design — a row that no longer matches an active
     * status filter stays visible until the next natural refetch. Showing it
     * with the correct status still beats showing it with a stale one.
     */
    shipmentStatusPatched: (
      state,
      action: PayloadAction<{
        shipmentId: string;
        status: string;
        updatedAt?: string;
      }>,
    ) => {
      const { shipmentId, status, updatedAt } = action.payload;

      // statusCounts is a fixed-key object; index it by name only after
      // confirming the key exists, so an unknown status can never create one.
      const counts: Record<string, number> = state.statusCounts;

      const row = state.shipments.find((s) => s._id === shipmentId);
      if (row && row.status !== status) {
        if (counts[row.status] !== undefined) counts[row.status] -= 1;
        if (counts[status] !== undefined) counts[status] += 1;

        row.status = status;
        // Keep the OCC token in step, or the next save from this tab would be
        // rejected as a conflict against a change it has already absorbed.
        if (updatedAt) row.updatedAt = updatedAt;
      }

      const current = state.currentShipment;
      if (current && current._id === shipmentId) {
        current.status = status;
        if (updatedAt) current.updatedAt = updatedAt;
      }
    },

    resetShipmentState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Get Shipments
      //
      // Responses are applied only if they belong to the newest request, so a
      // slow reply to a filter the user has already moved on from cannot
      // overwrite the list they are looking at now. Typing in the search box is
      // the easy way to provoke that: several fetches are in flight at once and
      // they need not come back in order.
      //
      // The rows are deliberately NOT cleared here. Leaving them in place keeps
      // the table stable while a filter is refined; blanking on every fetch made
      // the list flash empty on each keystroke.
      .addCase(getShipments.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.listRequestId = action.meta.requestId;
      })
      .addCase(getShipments.fulfilled, (state, action) => {
        if (state.listRequestId !== action.meta.requestId) return;
        state.loading = false;
        state.listRequestId = null;
        state.shipments = action.payload.shipments;
        state.pagination = action.payload.pagination;
        state.statusCounts = action.payload.statusCounts;
        state.error = null;
      })
      .addCase(getShipments.rejected, (state, action) => {
        if (state.listRequestId !== action.meta.requestId) return;
        state.loading = false;
        state.listRequestId = null;
        state.error = action.payload as string;
      })
      // Get Shipments For Groups
      .addCase(getShipmentsForGroups.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getShipmentsForGroups.fulfilled, (state, action) => {
        state.loading = false;
        state.shipments = action.payload.shipments;
        state.error = null;
      })
      .addCase(getShipmentsForGroups.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Delete Shipment
      .addCase(deleteShipment.fulfilled, (state, action) => {
        state.shipments = state.shipments.filter(
          (shipment) => shipment._id !== action.payload,
        );
        state.pagination.total -= 1;
        state.loading = false;
        state.error = null;
      })
      .addCase(deleteShipment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteShipment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Delete Multiple Shipments
      .addCase(deleteMultipleShipments.fulfilled, (state, action) => {
        state.shipments = state.shipments.filter(
          (shipment) => !action.payload.includes(shipment._id),
        );
        state.pagination.total -= action.payload.length;
        state.loading = false;
        state.error = null;
      })
      .addCase(deleteMultipleShipments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteMultipleShipments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Get Shipment
      .addCase(getShipment.pending, (state) => {
        state.currentShipmentLoadingForNotification = true;
        state.error = null;
      })
      .addCase(getShipment.fulfilled, (state, action) => {
        state.currentShipmentLoadingForNotification = false;
        state.currentShipment = action.payload;
        state.error = null;
      })
      .addCase(getShipment.rejected, (state, action) => {
        state.currentShipmentLoadingForNotification = false;
        state.error = action.payload as string;
      })
      // Get Public Shipment
      .addCase(getShipmentPublic.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getShipmentPublic.fulfilled, (state, action) => {
        state.loading = false;
        state.currentShipment = action.payload;
        state.error = null;
      })
      .addCase(getShipmentPublic.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Get Next Shipment Id
      .addCase(getNextShipmentId.pending, (state) => {
        state.fetchNextShipmentLoading = true;
        state.error = null;
      })
      .addCase(getNextShipmentId.fulfilled, (state) => {
        state.fetchNextShipmentLoading = false;
        state.error = null;
      })
      .addCase(getNextShipmentId.rejected, (state, action) => {
        state.fetchNextShipmentLoading = false;
        state.error = action.payload as string;
      })
      // Create Shipment
      .addCase(createShipment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createShipment.fulfilled, (state, action) => {
        state.loading = false;
        state.shipments.unshift(action.payload);
        state.pagination.total += 1;
        state.currentShipment = action.payload;
        state.error = null;
      })
      .addCase(createShipment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Get Shipment Copy Data (read-only prefill fetch)
      .addCase(getShipmentCopyData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getShipmentCopyData.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(getShipmentCopyData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Assign Shipment To Driver App
      .addCase(assignShipmentToDriverApp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(assignShipmentToDriverApp.fulfilled, (state, action) => {
        state.loading = false;
        const shipmentId = action.meta.arg;

        // Update shipments array if present
        const index = state.shipments.findIndex((s) => s._id === shipmentId);
        if (index !== -1) {
          state.shipments[index] = {
            ...state.shipments[index],
            assignedToDriverApp: true,
          };
        }

        // Always update current shipment if it matches
        if (state.currentShipment && state.currentShipment._id === shipmentId) {
          state.currentShipment = {
            ...state.currentShipment,
            assignedToDriverApp: true,
            // The server wrote to the shipment, so the version the open edit
            // form is holding is stale. Adopting the token this write returned
            // stops the user's next Save being refused as a conflict with
            // their own click.
            ...(action.payload?.data?.updatedAt
              ? { updatedAt: action.payload.data.updatedAt }
              : {}),
          };
        }
        state.error = null;
      })
      .addCase(assignShipmentToDriverApp.rejected, (state) => {
        state.loading = false;
      })
      // Remove Shipment From Driver App
      .addCase(removeShipmentFromDriverApp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeShipmentFromDriverApp.fulfilled, (state, action) => {
        state.loading = false;
        const shipmentId = action.meta.arg;

        // Update shipments array if present
        const index = state.shipments.findIndex((s) => s._id === shipmentId);
        if (index !== -1) {
          state.shipments[index] = {
            ...state.shipments[index],
            assignedToDriverApp: false,
          };
        }

        // Always update current shipment if it matches
        if (state.currentShipment && state.currentShipment._id === shipmentId) {
          state.currentShipment = {
            ...state.currentShipment,
            assignedToDriverApp: false,
            // The server wrote to the shipment, so the version the open edit
            // form is holding is stale. Adopting the token this write returned
            // stops the user's next Save being refused as a conflict with
            // their own click.
            ...(action.payload?.data?.updatedAt
              ? { updatedAt: action.payload.data.updatedAt }
              : {}),
          };
        }
        state.error = null;
      })
      .addCase(removeShipmentFromDriverApp.rejected, (state) => {
        state.loading = false;
      })
      // Update Shipment
      .addCase(updateShipment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateShipment.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.shipments.findIndex(
          (shipment) => shipment._id === action.payload._id,
        );
        if (index !== -1) {
          state.shipments[index] = action.payload;
        }
        state.currentShipment = action.payload;
        state.error = null;
      })
      .addCase(updateShipment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Generate XML
      .addCase(generateShipmentXML.pending, (state) => {
        state.error = null;
      })
      .addCase(generateShipmentXML.fulfilled, (state) => {
        state.error = null;
      })
      .addCase(generateShipmentXML.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      // Generate XML
      .addCase(fetchShipmentXMLFile.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchShipmentXMLFile.fulfilled, (state) => {
        state.error = null;
      })
      .addCase(fetchShipmentXMLFile.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      // Send CMR Attachments
      .addCase(sendCmrAttachments.pending, (state) => {
        state.sendEmailLoading = true;
        state.error = null;
      })
      .addCase(sendCmrAttachments.fulfilled, (state) => {
        state.sendEmailLoading = false;
        state.error = null;
      })
      .addCase(sendCmrAttachments.rejected, (state, action) => {
        state.sendEmailLoading = false;
        state.error = action.payload as string;
      })
      .addCase(sendDeliveryOtp.pending, (state) => {
        state.otpLoading = true;
        state.error = null;
      })
      .addCase(sendDeliveryOtp.fulfilled, (state, action) => {
        state.otpLoading = false;
        // Update currentShipment so the modal reflects the sent state immediately
        // without needing a refetch
        if (
          state.currentShipment &&
          state.currentShipment._id === action.payload.shipmentId
        ) {
          state.currentShipment = {
            ...state.currentShipment,
            deliveryOtp: {
              required: true,
              used: false,
              sentTo: action.payload.sentTo,
              sentAt: new Date().toISOString(),
            },
            // Sending the OTP was a write to this shipment — take the version it
            // produced so an open edit form does not later conflict with it.
            ...(action.payload.updatedAt
              ? { updatedAt: action.payload.updatedAt }
              : {}),
          };
        }
        state.error = null;
      })
      .addCase(sendDeliveryOtp.rejected, (state, action) => {
        state.otpLoading = false;
        state.error = action.payload as string;
      })
      .addCase(downloadShipmentAttachment.pending, (state) => {
        state.error = null;
      })
      .addCase(downloadShipmentAttachment.fulfilled, (state) => {
        state.error = null;
      })
      .addCase(downloadShipmentAttachment.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      // Send eFti
      .addCase(createWayBill.pending, (state) => {
        state.sendEmailLoading = true;
        state.error = null;
      })
      .addCase(createWayBill.fulfilled, (state, action) => {
        state.sendEmailLoading = false;
        const updated = action.payload;

        state.shipments = state.shipments.map((s) =>
          s._id === updated.shipmentId
            ? {
                ...s,
                waybill: {
                  status: "submitted",
                  datasetId: updated.datasetId,
                  submissionTime: updated.submissionTime,
                },
              }
            : s,
        );

        state.error = null;
      })
      .addCase(createWayBill.rejected, (state, action) => {
        state.sendEmailLoading = false;
        state.error = action.payload as string;
      })
      .addCase(getShipmentColumns.pending, (state) => {
        state.error = null;
      })
      .addCase(getShipmentColumns.fulfilled, (state, action) => {
        state.columns = action.payload;
        state.error = null;
      })
      .addCase(getShipmentColumns.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(updateShipmentColumns.pending, (state) => {
        state.error = null;
      })
      .addCase(updateShipmentColumns.fulfilled, (state, action) => {
        state.columns = action.payload;
        state.error = null;
      })
      .addCase(updateShipmentColumns.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(resetShipmentColumns.pending, (state) => {
        state.error = null;
      })
      .addCase(resetShipmentColumns.fulfilled, (state, action) => {
        state.columns = action.payload;
        state.error = null;
      })
      .addCase(resetShipmentColumns.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(toggleAttachmentPublic.fulfilled, (state, action) => {
        if (state.currentShipment?.attachments) {
          const att = state.currentShipment.attachments.find(
            (a) => a._id === action.payload.attachmentId,
          );
          if (att) att.isPublic = action.payload.isPublic;
        }
      })
      .addCase(uploadPublicAttachment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(uploadPublicAttachment.fulfilled, (state, action) => {
        state.loading = false;
        if (state.currentShipment) {
          state.currentShipment.publicAttachments = [
            ...(state.currentShipment.publicAttachments || []),
            ...action.payload,
          ];
        }
      })
      .addCase(uploadPublicAttachment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  clearCurrentShipment,
  setColumns,
  resetShipmentState,
  setLoading,
  shipmentStatusPatched,
} = shipmentSlice.actions;

export default shipmentSlice.reducer;
